import React from 'react';
import { strings } from '../../resources/strings';
import { SeparationType } from '../../enums/SeparationType';
import { OrderGeneralOption } from '../../enums/OrderGeneralOption';
import { DataSource } from '../../enums/DataSource';
import { ProfileType } from '../../enums/ProfileType';
import { StateOrderIdentification } from '../../enums/StateOrderIdentification';
import { useOrderUtils } from '../../hooks/useOrderUtils';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { SessionHelper } from '../../helpers/SessionHelper';
import { ReopenClosedOrderInput } from '../../services/Orders/ReopenClosedOrder/ReopenClosedOrderInput';
import { DropdownButton, DropdownButtonColor } from '../../common-components/DropdownButton/DropdownButton';
import { ModalOrderNotes } from '../ModalOrderNotes/ModalOrderNotes';
import { ModalChangeOrderCustomer } from '../ModalChangeOrderCustomer/ModalChangeOrderCustomer';
import { ModalProductGroups } from '../ModalProductGroups/ModalProductGroups';

interface IGeneralOptionsButtonProps {
  orderId?: number;
  orderNumber?: string;
  isPhysicalStore?: boolean;
  separationType?: SeparationType;
  customerId?: number;
  customerShopifyId?: number;
  orderDataSource?: DataSource;
  shopifyFormatedClosedAt?: string;
  modalOpened?: () => void;
  modalClosed?: (changed?: boolean) => void;
  handleGeneralOption?: (key: any) => void;
  orderIsCrossChannel?: boolean;
}

export const GeneralOptionsButton: React.FC<IGeneralOptionsButtonProps> = ({
  orderId,
  orderNumber,
  isPhysicalStore,
  separationType,
  customerId,
  customerShopifyId,
  orderDataSource,
  shopifyFormatedClosedAt,
  modalOpened,
  modalClosed,
  handleGeneralOption,
  orderIsCrossChannel
}) => {

  const { session } = useAuth();

  const [modalOrderNotesVisible, setModalOrderNotesVisible] = React.useState<boolean>(false);
  const [modalChangeCustomerVisible, setModalChangeCustomerVisible] = React.useState<boolean>(false);
  const [modalProductGroupsVisible, setModalProductGroupsVisible] = React.useState<boolean>(false);

  const {
    toastService,
    alertService,
    reopenClosedOrderService
  } = React.useContext(ServicesContext);
  const { printTSO } = useOrderUtils();

  const genericalOptionsItems = [];

  genericalOptionsItems.push({
    key: OrderGeneralOption.NewOrderNote,
    text: strings.NewNote
  });

  if (separationType === SeparationType.Prescription) {
    genericalOptionsItems.push({
      key: OrderGeneralOption.PrintTSO,
      text: `${strings.TSO} A4 (v1)`
    });
    genericalOptionsItems.push({
      key: OrderGeneralOption.PrintTSOV2A5,
      text: `${strings.TSO} A5 (v2)`
    });
  }

  if (shopifyFormatedClosedAt && session?.user?.profileType === ProfileType.Vendor && !orderIsCrossChannel) {
    genericalOptionsItems.push(
      { key: OrderGeneralOption.ReopenIncorrectlyDeliveredOrder, text: strings.ReopenIncorrectlyDeliveredOrder }
    );
  }

  if (separationType === SeparationType.Prescription && SessionHelper.canManageProductGroups(session) &&
    (!shopifyFormatedClosedAt || session?.user?.profileType == ProfileType.Admin)
  ) {
    genericalOptionsItems.push({
      key: OrderGeneralOption.ProductGroups,
      text: strings.ProductGroups
    });
  }

  return (
    <>
      <DropdownButton
        items={genericalOptionsItems}
        defaultStyle={true}
        small={true}
        buttonColor={DropdownButtonColor.Secondary}
        text={''}
        clickItem={(key) => applyGeneralOption(key)}
        marginRight={'3px'}
      />
      <ModalOrderNotes
        visible={modalOrderNotesVisible}
        orderId={orderId}
        title={strings.NewNote}
        notList
        close={(changed) => closeModalOrderNotes(changed)}
      />
      <ModalChangeOrderCustomer
        visible={modalChangeCustomerVisible}
        orderId={orderId}
        orderNumber={orderNumber}
        close={(changed) => closeModalChangeCustomerOrder(changed)}
      />
      <ModalProductGroups
        visible={modalProductGroupsVisible}
        orderId={orderId}
        title={strings.ProductGroups}
        close={(changed) => closeModalProductGroups(changed)}
      />
    </>
  );

  async function applyGeneralOption(key: any) {
    if (!orderId) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      return;
    }

    if (key === OrderGeneralOption.PrintTSO) {
      printTSO(orderId);
      return;
    }

    if (key === OrderGeneralOption.PrintTSOV2A5) {
      printTSO(orderId, 2);
      return;
    }

    if (key === OrderGeneralOption.NewOrderNote) {
      setModalOrderNotesVisible(true);
      if (modalOpened) modalOpened();
      return;
    }

    if (key === OrderGeneralOption.ChangeCustomer) {
      setModalChangeCustomerVisible(true);
      if (modalOpened) modalOpened();
      return;
    }

    if (key === OrderGeneralOption.ReopenIncorrectlyDeliveredOrder) {
      const confirmation = await alertService.confirm(`Deseja realmente reabrir o pedido entregue?`);
      if (!confirmation.isConfirmed) return;
      const input = new ReopenClosedOrderInput();
      input.orderId = orderId;
      input.newStateId = StateOrderIdentification.ProntoParaEnvio;
      input.userId = session?.user?.id;
      input.reason = `Entregue por engano`;
      reopenClosedOrderService.handle(input)
        .then(result => {
          alertService.info(`Operação de reabertura concluída!`);
          if (modalClosed) modalClosed(true);
        })
        .catch(error => {
          alertService.warning(`Ops! Não consegui realizar a reabertura do pedido. Tente novamente e se o problema perstir avise o time Tech.`);
        });
      return;
    }

    if (key === OrderGeneralOption.ProductGroups) {
      setModalProductGroupsVisible(true);
      if (modalOpened) modalOpened();
      return;
    }

    if (handleGeneralOption) handleGeneralOption(key);
  }

  function closeModalOrderNotes(changed?: boolean) {
    setModalOrderNotesVisible(false);
    if (modalClosed) modalClosed(changed);
  }

  function closeModalChangeCustomerOrder(changed?: boolean) {
    setModalChangeCustomerVisible(false);
    if (modalClosed) modalClosed(changed);
  }

  function closeModalProductGroups(changed?: boolean) {
    setModalProductGroupsVisible(false);
    if (modalClosed) modalClosed(changed);
  }
};